import { Box, Button, Dialog, FormControl, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useClientId } from '../../components/auth/useClientid';
import { CaseHistoryProvider } from '../manualTasksV2/caseHistory/CaseHistoryContext';
import { CaseHistory } from '../manualTasksV2/tabs/CaseHistory';

export enum SearchIdType {
    LOS_APP_ID = 'LOS_APP_ID',
    PAN = 'PAN',
    CUSTOMER_ID = 'CUSTOMER_ID',
    UCIC = 'UCIC',
    PROVIDER_REF_ID = 'PROVIDER_REF_ID',
    CKYC_NUMBER = 'CKYC_NUMBER',
}

interface GlobalSearchFormData {
    searchId: string;
    searchIdType: SearchIdType;
}

export const GlobalSearch: React.FC = () => {
    const { clientId } = useClientId();
    const [formData, setFormData] = useState<GlobalSearchFormData>({
        searchId: '',
        searchIdType: SearchIdType.LOS_APP_ID,
    });
    const [isModalOpen, setIsModalOpen] = useState(false);

    const handleSubmit = (e: React.FormEvent): void => {
        e.preventDefault();
        if (formData.searchId && formData.searchIdType) {
            setIsModalOpen(true);
        }
    };

    return (
        <Box
            component={'form'}
            onSubmit={handleSubmit}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                width: '50%',
                margin: '40px auto',
                padding: '20px',
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}>
            <Typography variant={'h5'} sx={{ mb: 2 }}>
                Global Case Search
            </Typography>

            <FormControl fullWidth>
                <InputLabel>Search ID Type</InputLabel>
                <Select
                    value={formData.searchIdType}
                    label={'Search ID Type'}
                    onChange={(e): void =>
                        setFormData((prev) => ({ ...prev, searchIdType: e.target.value as SearchIdType }))
                    }>
                    <MenuItem value={SearchIdType.LOS_APP_ID}>LOS Application ID</MenuItem>
                    <MenuItem value={SearchIdType.CUSTOMER_ID}>Customer ID</MenuItem>
                    <MenuItem value={SearchIdType.PAN}>PAN</MenuItem>
                    <MenuItem value={SearchIdType.UCIC}>UCIC</MenuItem>
                    <MenuItem value={SearchIdType.PROVIDER_REF_ID}>Provider Reference ID</MenuItem>
                    <MenuItem value={SearchIdType.CKYC_NUMBER}>CKYC Number</MenuItem>
                </Select>
            </FormControl>

            <TextField
                fullWidth
                label={'Search ID'}
                value={formData.searchId}
                onChange={(e): void => setFormData((prev) => ({ ...prev, searchId: e.target.value }))}
                required
            />

            <Button
                type={'submit'}
                variant={'contained'}
                sx={{ mt: 2 }}
                disabled={!formData.searchId || !formData.searchIdType}>
                Search Case History
            </Button>

            <Dialog
                open={isModalOpen}
                onClose={(): void => setIsModalOpen(false)}
                maxWidth={'md'}
                fullWidth
                PaperProps={{
                    sx: {
                        height: '90vh',
                        maxHeight: '90vh',
                        display: 'flex',
                        flexDirection: 'column',
                    },
                }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', flex: '0 0 auto' }}>
                        <Typography variant={'h6'}>Case History ({formData.searchIdType})</Typography>
                    </Box>
                    <Box sx={{ flex: '1 1 auto', overflow: 'hidden', height: '100%' }}>
                        <CaseHistoryProvider>
                            <CaseHistory
                                clientId={clientId ?? ''}
                                searchId={formData.searchId}
                                searchIdType={formData.searchIdType}
                            />
                        </CaseHistoryProvider>
                    </Box>
                    <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', flex: '0 0 auto' }}>
                        <Button onClick={(): void => setIsModalOpen(false)} variant={'outlined'}>
                            Close
                        </Button>
                    </Box>
                </Box>
            </Dialog>
        </Box>
    );
};
